/**
 * Course EPECLs. Each lane has 5–10 instructor prompts covering the
 * T&R events listed for that lane in the updated platform note.
 * Prompts say what to judge. They do not describe how to do the task.
 * RECN-SERE-2001 and RECN-SERE-2007 are classified in that note, so
 * those prompts stay on the briefed action and do not add steps.
 */

export interface RubricCategory {
  id: string;
  title: string;
  prompt: string;
  /** Event codes this prompt is responsible for. */
  tasks: string[];
  /** Short attributes the instructor weighs. Not a procedure. */
  lookFor: string[];
}

export interface LaneDef {
  id: string;
  title: string;
  categories: RubricCategory[];
}

/** Wording saved with one evaluation after the instructor edits the checklist. */
export interface SavedCriterion {
  id: string;
  title: string;
  prompt: string;
  lookFor: string[];
}

export function defaultCriteria(laneId: string): SavedCriterion[] {
  return (laneById(laneId)?.categories || []).map((cat) => ({
    id: cat.id,
    title: cat.title,
    prompt: cat.prompt,
    lookFor: [...cat.lookFor],
  }));
}

export const REFERENCES = [
  'MCRP 2-10A.6 Ground Reconnaissance Operations',
  'MCTP 3-01A Scouting and Patrolling (Jul 2020)',
  'NAVMC 3500.55D Reconnaissance T&R Manual',
];

function lookFor(id: string, title: string): string[] {
  if (id.startsWith('demo-')) {
    return [
      'Whether the safety brief held from the start through the end',
      'Whether anyone had to stop the work',
      'Whether the result matched what this lane was for',
      'Whether the Marine in charge stayed in control',
    ];
  }
  if (id === 'eva-evade' || id === 'eva-recover' || id === 'eva-contact') {
    return [
      'Whether they followed the action that was briefed',
      'Whether they stayed with the evasion plan',
      'Whether people stayed accounted for',
      'Whether the lane had to be stopped',
    ];
  }
  const name = title.toLowerCase();
  return [
    `Whether ${name} matched what this lane asked for`,
    'Whether it was finished, or only started',
    'Whether the Marines doing it stayed under control',
    'Whether the result was specific enough to use later',
  ];
}

function cat(
  id: string,
  title: string,
  prompt: string,
  tasks: string[],
  lines?: string[]
): RubricCategory {
  return { id, title, prompt, tasks, lookFor: lines?.length ? lines : lookFor(id, title) };
}

export const LANES: LaneDef[] = [
  {
    id: 'cas-cff',
    title: 'CAS/CFF Simulator',
    categories: [
      cat(
        'cff-plan',
        'Fire support plan',
        'Did the fire support plan match the mission and stay usable when the lane changed?',
        ['RECN-FSPT-2401']
      ),
      cat(
        'cff-targets',
        'Target list',
        'Was the target list complete, prioritized, and understood by the people who had to shoot it?',
        ['RECN-FSPT-2402']
      ),
      cat(
        'cff-schedule',
        'Schedule of fires',
        'Did the schedule of fires sequence the effects the lane required, with a way to shift?',
        ['RECN-FSPT-2403']
      ),
      cat(
        'cff-overlay',
        'Coordination overlay',
        'Did the overlay keep fires, air, and the ground force from working against each other?',
        ['RECN-FSPT-2501']
      ),
      cat(
        'cff-call',
        'Call for fire',
        'Were grid, polar, and shift calls clear, complete, and corrected when the observer had to adjust?',
        ['RECN-SHAP-1001', 'RECN-SHAP-1002', 'RECN-SHAP-1005']
      ),
      cat(
        'cff-cas',
        'Close air support',
        'Did the non-TAC Marine bring the aircraft onto the target the lane assigned, under control?',
        ['RECN-SHAP-2004']
      ),
      cat(
        'cff-locate',
        'Target location',
        'Did they get a usable target location from the equipment, and know when the location was bad?',
        ['RECN-SHAP-2006']
      ),
      cat(
        'cff-mark',
        'Marking',
        'Was the mark seen by the firing agency and unmistakably the target, not friendly positions?',
        ['RECN-SHAP-2007']
      ),
    ],
  },
  {
    id: 'planning-mdrs',
    title: 'Planning for Multi-Domain R&S',
    categories: [
      cat(
        'plan-prep',
        'Preparation for combat',
        'Did individuals and the unit show up ready, with the leader actually checking the prep?',
        ['RECN-C2-1001', 'RECN-C2-2407']
      ),
      cat(
        'plan-route',
        'Route plan and overlay',
        'Was the route planned, drawn, and evaluated, on paper and in the digital overlay?',
        ['RECN-C2-1002', 'RECN-C2-1003', 'RECN-C2-2002', 'RECN-C2-2408', 'RECN-C2-2409']
      ),
      cat(
        'plan-terrain',
        'Terrain model',
        'Did the terrain model or visual aid let the unit see the ground, and did someone evaluate it?',
        ['RECN-C2-1004', 'RECN-C2-2412']
      ),
      cat(
        'plan-orders-patrol',
        'Warning order and patrol order',
        'Were the warning order and patrol order issued in time for the unit to prepare?',
        ['RECN-C2-2406', 'RECN-C2-2413', 'RECN-C2-2414']
      ),
      cat(
        'plan-sustain',
        'Communications and sustainment',
        'Did the communications plan and the sustainment plan cover the duration of the mission?',
        ['RECN-C2-2410', 'RECN-C2-2411']
      ),
      cat(
        'plan-detail',
        'Detailed planning and confirmation',
        'Did detailed ground reconnaissance planning survive a confirmation brief?',
        ['RECN-C2-2512', 'RECN-C2-2515']
      ),
      cat(
        'plan-opord',
        'Operations order',
        'Was the operations order produced and issued so the unit could execute it?',
        ['RECN-C2-2513', 'RECN-C2-2514']
      ),
      cat(
        'plan-ready',
        'Prepare for operations',
        'At the end of planning, was the unit actually prepared to step off?',
        ['RECN-C2-4001']
      ),
    ],
  },
  {
    id: 'tak',
    title: 'Tactical Assault Kit',
    categories: [
      cat(
        'tak-route',
        'Digital route and overlay',
        'Did the route plan and overlay in the kit match the ground, and did someone check them?',
        ['RECN-C2-1002', 'RECN-C2-1003', 'RECN-C2-2002', 'RECN-C2-2408', 'RECN-C2-2409']
      ),
      cat(
        'tak-terrain',
        'Visual aid',
        'Could the unit see the terrain model or visual aid in the kit and use it to brief?',
        ['RECN-C2-1004', 'RECN-C2-2412']
      ),
      cat(
        'tak-sitmap',
        'Situation map',
        'Did the situation map stay current enough that a new watcher could take the picture over?',
        ['RECN-C2-2504']
      ),
      cat(
        'tak-current',
        'Picture stayed current',
        'When the situation changed, did the kit get updated before people were working off the old picture?',
        ['RECN-C2-2504', 'RECN-C2-5002']
      ),
      cat(
        'tak-c2',
        'Platoon command and control',
        'Could the platoon leader run the unit from the kit without losing teams or reports?',
        ['RECN-C2-5002']
      ),
    ],
  },
  {
    id: 'demolitions',
    title: 'Demolitions',
    categories: [
      cat(
        'demo-pyro',
        'Employ pyrotechnics',
        'Did they employ pyrotechnics for the lane, under control, without a safety break?',
        ['RECN-DEMO-1001']
      ),
      cat(
        'demo-nonel',
        'Non-electric initiation set',
        'Was the initiation set complete, inspected, and safe to use for this lane?',
        ['RECN-DEMO-1005']
      ),
      cat(
        'demo-misfire',
        'Misfire',
        'When the lane called for it, did they make the misfire safe and keep everyone under control?',
        ['RECN-DEMO-1007']
      ),
      cat(
        'demo-detcord',
        'Detonation-cord firing system',
        'Did the single firing system do what the lane required, and stay under the firer’s control?',
        ['RECN-DEMO-1008']
      ),
      cat(
        'demo-prime',
        'Prime explosives',
        'Was priming correct for the shot they were given, and did anyone have to stop them for safety?',
        ['0311-DEMO-2002']
      ),
      cat(
        'demo-direct',
        'Direct the shot',
        'Were the leader’s directions clear, and did the team follow them without crowding the shot?',
        ['RECN-DEMO-2401']
      ),
      cat(
        'demo-employ',
        'Employ demolitions',
        'Did the shot achieve the lane’s purpose, and did the safety brief hold through the end?',
        ['RECN-DEMO-3001']
      ),
    ],
  },
  {
    id: 'linkup',
    title: 'Conduct Linkup Procedures',
    categories: [
      cat(
        'lu-plan',
        'Linkup plan',
        'Did the element know the linkup point, the time, and the far-side unit before they moved?',
        ['RECN-GRND-5005']
      ),
      cat(
        'lu-signals',
        'Recognition and signals',
        'Were recognition and signals clear enough that the two sides identified each other without confusion?',
        ['RECN-GRND-5005']
      ),
      cat(
        'lu-security',
        'Security',
        'Did security hold while the linkup was happening, including a way to break contact?',
        ['RECN-GRND-5005']
      ),
      cat(
        'lu-account',
        'Accountability',
        'Was every Marine accounted for before the linkup was called complete?',
        ['RECN-GRND-5005']
      ),
      cat(
        'lu-after',
        'Actions after linkup',
        'After the linkup, did they pass what the other unit needed and leave the point under control?',
        ['RECN-GRND-5005']
      ),
    ],
  },
  {
    id: 'drop-zone',
    title: 'Establish a Drop Zone',
    categories: [
      cat(
        'dz-individual',
        'Individual actions',
        'Did each Marine do their own job while the drop zone was being stood up?',
        ['RECN-GRND-1023']
      ),
      cat(
        'dz-insert',
        'Parachute insertion',
        'Did the insertion into the special-purpose drop zone happen as briefed, with accountability right after?',
        ['RECN-SPIE-2002']
      ),
      cat(
        'dz-mark',
        'Marking',
        'Was the drop zone marked the way the lane required, and did someone actually supervise it?',
        ['RECN-GRND-2412']
      ),
      cat(
        'dz-establish',
        'Establish the drop zone',
        'Was the drop zone usable — control, signals, and security — by the time it needed to be?',
        ['RECN-GRND-4027']
      ),
      cat(
        'dz-lead',
        'Run the drop zone',
        'Did the leader keep the drop zone running, including a clear call when it was open or closed?',
        ['RECN-GRND-5007']
      ),
    ],
  },
  {
    id: 'hide-site',
    title: 'Hide Site Operations',
    categories: [
      cat(
        'hs-establish',
        'Establish the observation post',
        'Did they establish an observation post that can see the objective and still be defended?',
        ['RECN-GRND-1013', 'RECN-GRND-3001']
      ),
      cat(
        'hs-supervise',
        'Supervise the post',
        'Did a leader supervise the post — reliefs, sectors, and the log — for as long as they were in it?',
        ['RECN-GRND-2407']
      ),
      cat(
        'hs-conceal',
        'Concealment',
        'Did the hide stay hidden — noise, light, trash, and movement — for the time they occupied it?',
        ['RECN-GRND-3001', 'RECN-GRND-1013']
      ),
      cat(
        'hs-sniper-team',
        'Sniper team in the hide',
        'Did the sniper team run its own routine inside the hide without giving the site away?',
        ['RECN-SHAP-3001']
      ),
      cat(
        'hs-sniper-ops',
        'Sniper operations from the site',
        'Did sniper employment from the hide match the task, including security of the firing position?',
        ['RECN-SHAP-5002']
      ),
    ],
  },
  {
    id: 'sniper',
    title: 'Sniper Integration',
    categories: [
      cat(
        'si-task',
        'Task and position',
        'Were the snipers given a clear task and a position that supports the platoon plan?',
        ['RECN-SHAP-3001']
      ),
      cat(
        'si-routine',
        'Team routine',
        'Did the sniper team run its routine — observer and firer, security, communications — without the platoon having to remind them?',
        ['RECN-SHAP-3001']
      ),
      cat(
        'si-marksmanship',
        'Advanced marksmanship',
        'Did engagements use the advanced marksmanship the lane called for, and stay inside the briefed limits?',
        ['RECN-SNPR-2001']
      ),
      cat(
        'si-scheme',
        'Fit with the platoon',
        'Did sniper operations support the scheme of maneuver instead of becoming a separate fight?',
        ['RECN-SHAP-4002']
      ),
      cat(
        'si-control',
        'Control and displacement',
        'Did the leader control the snipers through displacement and rejoin, without losing them?',
        ['RECN-SHAP-5002']
      ),
    ],
  },
  {
    id: 'ambush',
    title: 'Conduct Ambushes',
    categories: [
      cat(
        'amb-direct',
        'Direction',
        'Did the leader direct the ambush — site, trigger, and who shoots — before occupation?',
        ['RECN-SHAP-2501']
      ),
      cat(
        'amb-occupy',
        'Occupation and security',
        'Was the site occupied quietly, with security that would have seen a counter-ambush coming?',
        ['RECN-SHAP-4007', 'RECN-SHAP-5007']
      ),
      cat(
        'amb-init',
        'Initiation',
        'Did initiation happen on the trigger they briefed, and did fires start together?',
        ['RECN-SHAP-4007', 'RECN-SHAP-5007']
      ),
      cat(
        'amb-zone',
        'Actions in the kill zone',
        'Did actions in the kill zone match the plan, including the search and the cease-fire?',
        ['RECN-SHAP-4001', 'RECN-SHAP-4007']
      ),
      cat(
        'amb-leave',
        'Withdrawal and accountability',
        'Did they leave the site on the planned route and account for everyone before moving far?',
        ['RECN-SHAP-4007', 'RECN-SHAP-5007']
      ),
      cat(
        'amb-lead',
        'Leader control',
        'Did the leader keep control of the ambush from occupation through the withdrawal?',
        ['RECN-SHAP-4001', 'RECN-SHAP-5007']
      ),
    ],
  },
  {
    id: 'raid',
    title: 'Conduct Limited-Scale Raids',
    categories: [
      cat(
        'raid-individual',
        'Individual actions',
        'Did each Marine perform the individual actions the raid assigned them?',
        ['RECN-RAID-2004']
      ),
      cat(
        'raid-lead',
        'Leader direction',
        'Did the leader direct the raid so assault, support, and security heard the same plan?',
        ['RECN-RAID-2401']
      ),
      cat(
        'raid-assault',
        'Assault element',
        'Did the assault element do its job on the objective and stop when the leader called it?',
        ['RECN-RAID-4001', 'RECN-RAID-5002']
      ),
      cat(
        'raid-support',
        'Support element',
        'Did support isolate the objective and lift or shift when the assault needed it?',
        ['RECN-RAID-4002', 'RECN-RAID-5003']
      ),
      cat(
        'raid-security',
        'Security element',
        'Did security hold the approaches and still cover the withdrawal?',
        ['RECN-RAID-4003', 'RECN-RAID-5004']
      ),
      cat(
        'raid-exploit',
        'Exploitation',
        'Was site exploitation deliberate, and was sensitive material handled as briefed?',
        ['RECN-RAID-4004', 'RECN-RAID-5005']
      ),
      cat(
        'raid-tco',
        'Tactical call out',
        'If the lane used a call out, did it stay controlled and produce the people or information it was for?',
        ['RECN-RAID-5007']
      ),
      cat(
        'raid-whole',
        'The raid',
        'Taken together, did the specialized raid achieve its purpose and get the unit off the objective?',
        ['RECN-RAID-5001']
      ),
    ],
  },
  {
    id: 'evasion',
    title: 'Conduct Evasion and Recovery',
    categories: [
      cat(
        'epa-1',
        'Mission analysis',
        'Did the evasion plan come from this mission and this environment, rather than from a template?',
        ['RECN-SERE-2501'],
        [
          'Critical event',
          'Whether they identified likely isolation from insertion through the return',
          'Whether mission, enemy, terrain, weather, support, time, and civilians shaped evasion and recovery',
          'Whether they identified friendly forces, recovery forces, and what limited both',
          'Whether terrain and threat shaped movement, concealment, contact, and recovery',
          'Whether they could explain how that analysis drove the plan',
        ]
      ),
      cat(
        'epa-2',
        'EPA completeness',
        'Could a recovery force use this plan as written, with this mission’s details, not a pointer to another document?',
        ['RECN-SERE-2501'],
        [
          'Critical event',
          'Whether identification and accountability for the element were complete',
          'Whether planned routes covered the mission phases that applied',
          'Whether the first 48 hours were covered for both uninjured and injured people',
          'Whether sustained evasion or a delayed recovery was covered after that',
          'Whether communications, authentication, contact, and recovery were concrete enough for a recovery force',
        ]
      ),
      cat(
        'epa-3',
        'Starting evasion',
        'When the trigger came, did they start the plan without waiting for perfect conditions or more direction?',
        ['RECN-SERE-3001', 'RECN-SERE-4001'],
        [
          'Critical event',
          'Whether they recognized the trigger to start evasion and recovery',
          'Whether people, communications gear, survival gear, and mission-essential items were accounted for',
          'Whether they left mission execution for the plan without a long, confused pause',
          'Whether the first priorities were security, accountability, casualties, communications, and the movement decision',
          'Whether the decision and the responsibilities were clear inside the team',
        ]
      ),
      cat(
        'epa-4',
        'Evasion route',
        'Can they explain a corridor and routes that the terrain, the threat, and the recovery concept can support?',
        ['RECN-SERE-2005'],
        [
          'Critical event',
          'Whether the primary corridor fit terrain, threat, mobility, and recovery',
          'Whether primary and alternate routes reached rally, contact, recovery, or friendly forces',
          'Whether handrails, checkpoints, backstops, and control measures supported navigation',
          'Whether obstacles, restricted ground, built-up areas, traffic, and threat control were accounted for',
          'Whether decision points told them when to change route, switch plans, or move to recovery',
        ]
      ),
      cat(
        'epa-5',
        'Recovery sites',
        'Did they choose recovery locations and say why, rather than only point at them on the map?',
        ['RECN-SERE-2005'],
        [
          'Critical event',
          'Whether they identified the assigned SAFE, DAR, recovery point, or other recovery area',
          'Whether the place supported security, concealment, access, navigation, and recovery forces',
          'Whether there was a primary and an alternate contact location',
          'Whether restrictions, threat, weather, terrain, and recovery-force limits were accounted for',
          'Whether they stated what would make them bypass, leave, or shift off a recovery location',
        ]
      ),
      cat(
        'epa-6',
        'Communications',
        'If the primary method failed, could they still authenticate and make contact using only notional training data?',
        [],
        [
          'Critical event',
          'Whether primary, alternate, contingency, and emergency methods were set',
          'Whether windows, listening periods, transmissions, and no-communications actions were set',
          'Whether they could explain notional authentication and bona fides with approved training data only',
          'Whether recovery activation, contact, and linkup were identified',
          'Whether they shifted to an alternate method when the primary was down or compromised',
        ]
      ),
      cat(
        'epa-7',
        'Injured evader',
        'Is the plan for an injured person different from the uninjured plan, and realistic for this scenario?',
        ['RECN-SERE-2013'],
        [
          'Critical event',
          'Whether the first casualty check changed the plan',
          'Whether route, technique, pace, timeline, and recovery changed for the casualty',
          'Whether movement, treatment, and redistribution of equipment were identified',
          'Whether they stated when to keep moving, hold, split, or ask for recovery',
          'Whether communications and recovery matched the casualty’s condition and mobility',
        ]
      ),
      cat(
        'epa-8',
        'Separation and rally',
        'Does the plan still work if the team does not stay together?',
        [],
        [
          'Whether they planned for staying together, splitting, or individual isolation',
          'Whether initial and alternate rally points, timing, and linkup were identified',
          'Whether accountability, communications, navigation, and medical support were assigned after a split',
          'Whether a missing member had actions assigned',
          'Whether separated people could rejoin the recovery plan without exposing the rest of the team',
        ]
      ),
      cat(
        'epa-9',
        'Movement',
        'Did movement follow the approved plan, and did they change route or timing when the risk changed?',
        ['RECN-SERE-2001', 'RECN-SERE-2003', 'RECN-SERE-2004', 'RECN-SERE-2005'],
        [
          'Whether they followed the briefed evasion action',
          'Whether movement matched the plan, the timeline, and the scenario',
          'Whether map, compass, terrain association, or another authorized method was used correctly',
          'Whether they noticed terrain, threat indicators, observation, and likely detection points',
          'Whether progress was balanced with concealment, security, energy, and sustainment',
          'Whether route or timing changed when the risk picture changed',
          'If a sentry or a dog was part of the lane, whether that contact was handled as briefed',
        ]
      ),
      cat(
        'epa-10',
        'Hole-up and sustainment',
        'Could they sustain the plan past the first move?',
        ['RECN-SERE-2008', 'RECN-SERE-2009', 'RECN-SERE-2012', 'RECN-SERE-2013'],
        [
          'Whether the stop supported concealment, security, control of movement, and recovery',
          'Whether accountability, security, communications, medical status, and the next move were set',
          'Whether water, food, shelter, medical, and equipment needs fit the scenario',
          'Whether survival equipment was managed, and improvisation was used only when it was necessary',
          'Whether emergency resupply was identified, including how it would be asked for or received',
        ]
      ),
      cat(
        'epa-11',
        'Plan changes',
        'When the lane broke the route, the site, the radio, the casualty, or the timeline, did a recovery force still have a coherent plan?',
        ['RECN-SERE-3001', 'RECN-SERE-4001'],
        [
          'Critical event',
          'Whether they noticed when an inject invalidated the route, site, communications, or timeline',
          'Whether the update kept accountability, security, and a plan a recovery force could predict',
          'Whether they shifted to an alternate route, site, or communication method when required',
          'Whether each change was explained from the mission, enemy, terrain, weather, support, time, and civilians',
          'Whether the recovery concept held together after more than one change',
        ]
      ),
      cat(
        'epa-12',
        'Recovery linkup',
        'Did the notional linkup stay controlled, with accountability, a short status, and the debrief the recovery needed?',
        ['RECN-SERE-2006', 'RECN-SERE-2007'],
        [
          'Whether they followed the briefed recovery',
          'Whether the notional contact and authentication sequence was correct',
          'Whether the team was positioned and ready for a safe linkup',
          'Whether accountability and casualty control held during preparation',
          'Whether the status report covered location, intentions, condition, and support needed',
          'Whether post-recovery debrief needs were identified, including medical and operational points that applied',
        ]
      ),
    ],
  },
  {
    id: 'recon-patrol',
    title: 'Conduct a Reconnaissance Patrol',
    categories: [
      cat(
        'rp-1',
        'Mission analysis',
        'Did task organization come from the mission, the commander’s intent, and the collection requirements?',
        ['RECN-C2-2512', '0321-PAT-4101'],
        [
          'Critical event',
          'Roles: team leader, assistant team leader, radio operators, point, scouts, and SARC when assigned',
          'Whether mission, intent, priority intelligence requirements, and assigned recon or surveillance tasks were identified',
          'Whether named areas of interest, targets, observation requirements, reportable information, and the timeline were identified',
          'Whether mission, enemy, terrain, weather, support, time, and civilians were analyzed',
          'Whether duties, equipment, sectors, collection tasks, and alternates were assigned by billet',
          'Whether they could explain how that organization supports collection, survivability, communications, sustainment, and recovery',
        ]
      ),
      cat(
        'rp-2',
        'Individual preparation',
        'Before stepping off, did every Marine know the job and carry gear that had actually been inspected?',
        ['RECN-C2-1001', 'RECN-C2-2407', 'RECN-C2-4001'],
        [
          'Critical event',
          'Roles: every member of the patrol',
          'Whether weapons, radios, optics, navigation aids, medical gear, and mission equipment were prepared',
          'Whether inspections caught missing, damaged, or wrongly set equipment',
          'Whether loads, sustainment, batteries, water, and special equipment were confirmed',
          'Whether rehearsals happened and the faults found there were fixed',
          'Whether each Marine could explain duties, reports, immediate actions, and contingencies',
        ]
      ),
      cat(
        'rp-3',
        'Patrol order',
        'Could each billet brief back the order, the route, and what they do when the plan changes?',
        [
          'RECN-C2-2406',
          'RECN-C2-2413',
          'RECN-C2-2414',
          'RECN-C2-2513',
          'RECN-C2-2514',
          'RECN-C2-2515',
          'RECN-C2-1004',
          'RECN-C2-2002',
          'RECN-C2-2412',
        ],
        [
          'Critical event',
          'Roles: team leader, assistant team leader, radio operators, point, scouts, and SARC',
          'Whether the order, intent, concept, and control measures were understood',
          'Whether brief-backs confirmed each duty and each phase',
          'Whether primary and alternate actions covered movement, communications, security, casualties, a lost Marine, and extraction',
          'Whether a terrain model, map, overlay, or other aid showed routes, rally points, the patrol base, the observation post, and extraction',
          'Whether gaps were questioned and the plan was refined before execution',
        ]
      ),
      cat(
        'rp-4',
        'Communications',
        'If the primary radio failed, could the patrol still report on time and keep a log?',
        [
          'RECN-C2-2410',
          '0321-COMM-2201',
          'RECN-COMM-1003',
          'RECN-COMM-1006',
          'RECN-COMM-1009',
          'RECN-COMM-1013',
          'RECN-COMM-2407',
          'RECN-C2-2502',
          'RECN-C2-2503',
          'RECN-C2-2511',
        ],
        [
          'Critical event',
          'Roles: radio operator and assistant; team leader and assistant team leader supervise',
          'Whether primary, alternate, contingency, and emergency methods were set',
          'Whether programming, nets, checks, windows, and the reporting schedule were confirmed',
          'Whether failure, lost communications, a frequency change, and an alternate method were identified',
          'Whether visual signals, internal calls, time hacks, and report formats were understood',
          'Whether radio discipline, accountability, and a communications log were kept',
        ]
      ),
      cat(
        'rp-5',
        'Route and navigation',
        'Did the route serve the collection requirement, and did the team leader get timely corrections?',
        ['RECN-C2-1002', 'RECN-C2-1003', 'RECN-C2-2408', 'RECN-C2-2409', 'RECN-GRND-1008', 'RECN-GRND-1009', 'RECN-GRND-1010'],
        [
          'Critical event',
          'Roles: point, team leader, assistant team leader, scouts, and radio operator',
          'Whether primary and alternate routes fit the mission, terrain, threat, time, and collection',
          'Whether checkpoints, handrails, attack points, backstops, rally points, and control measures held movement',
          'Whether terrain association, map, compass, and authorized GPS or dead reckoning were used correctly',
          'Whether location, progress, time, and distance stayed known',
          'Whether deviations and location reports reached the team leader in time',
        ]
      ),
      cat(
        'rp-6',
        'Tactical movement',
        'Did the patrol move in a way that kept security and did not announce itself?',
        ['RECN-GRND-1001', 'RECN-GRND-1002', 'RECN-GRND-1003', 'RECN-GRND-1016', 'RECN-GRND-1017', 'RECN-GRND-4002'],
        [
          'Critical event',
          'Roles: every member of the patrol',
          'Whether formation, spacing, and tempo fit the terrain, visibility, threat, and mission',
          'Whether camouflage, noise, light, and equipment discipline held',
          'Whether weapons and optics stayed ready without giving the patrol away',
          'Whether danger areas, obstacles, and open ground were crossed as planned',
          'Whether compromise, enemy activity, tracking, or a change in the area was detected and reported',
        ]
      ),
      cat(
        'rp-7',
        'Halts and patrol base',
        'At the halt, rally point, patrol base, or ORP, did security and accountability hold, and was the site sterilized before leaving?',
        ['RECN-GRND-5004', 'RECN-GRND-2421'],
        [
          'Critical event',
          'Roles: team leader, assistant team leader, point, scouts, radio operator, and SARC',
          'Whether short and long halts kept accountability, security, communications, and control',
          'Whether rally points and the objective rally point were occupied as planned',
          'Whether sectors, control, and duties were set at the patrol base or ORP',
          'Whether positions were improved and required sketches, range cards, or logs were started',
          'Whether the site was sterilized before the next move or the withdrawal',
        ]
      ),
      cat(
        'rp-8',
        'Observation post',
        'Did they recon the site before occupying it, and could they still see the requirement and get out?',
        ['0321-RECN-4002', 'RECN-GRND-1013', 'RECN-GRND-2407', 'RECN-GRND-3001'],
        [
          'Critical event',
          'Roles: team leader, assistant team leader, scouts, radio operators, and point',
          'Whether the site supported observation, reporting, security, concealment, sustainment, and withdrawal',
          'Whether the tentative location was reconnoitered before occupation',
          'Whether occupation did not compromise the patrol or the observation requirement',
          'Whether observation sectors, security, equipment, communications, and duties were set',
          'Whether an alternate site and withdrawal routes were identified',
        ]
      ),
      cat(
        'rp-9',
        'Collection',
        'Did they collect what was assigned, keep watching, and separate what they saw from what they assumed?',
        [
          '0321-RECN-4001',
          'RECN-GRND-4001',
          'RECN-GRND-5001',
          'RECN-GRND-4007',
          'RECN-GRND-5008',
          'RECN-PHOT-1001',
          'RECN-PHOT-1002',
          'RECN-PHOT-1003',
          'RECN-PHOT-1004',
          'RECN-PHOT-3001',
          'RECN-PHOT-3002',
          'RECN-PHOT-3003',
          'RECN-SURV-1001',
          'RECN-SURV-1002',
          'RECN-SURV-1003',
          'RECN-SURV-1005',
          'RECN-SURV-1007',
          'RECN-SURV-2003',
          'RECN-SURV-2004',
          'RECN-SURV-2401',
          'RECN-SURV-2403',
          'RECN-SURV-4001',
          'RECN-SURV-5001',
        ],
        [
          'Critical event',
          'Roles: team leader, assistant team leader, scouts, and radio operators',
          'Whether assigned targets, areas, routes, terrain, or threat activity were observed',
          'Whether observation continued without giving up security or concealment',
          'Whether optics, imagery, sensors, sketches, and logs were used correctly',
          'Whether notes recorded what was seen, not assumptions',
          'Whether they could say if the requirement was met and what still had to be collected',
        ]
      ),
      cat(
        'rp-10',
        'Reporting',
        'Were the reports and the patrol log accurate, complete, and usable by higher headquarters?',
        [
          '0321-COMM-1207',
          '0321-INTL-2501',
          'RECN-COLL-1024',
          'RECN-GRND-1022',
          'RECN-C2-2504',
          'RECN-C2-2505',
          'RECN-INTL-2001',
          'RECN-INTL-2501',
          'RECN-INTL-2510',
          'RECN-INTL-4001',
          'RECN-INTL-4002',
          'RECN-INTL-5001',
        ],
        [
          'Critical event',
          'Roles: radio operators, team leader, assistant team leader, and assigned scouts',
          'Whether reportable information went out inside the required time',
          'Whether the format, location, and date-time group were right, without unsupported conclusions',
          'Whether the patrol log captured route, events, enemy activity, equipment, communications, and extraction',
          'Whether imagery, sketches, map corrections, and overlay changes were kept',
          'Whether higher headquarters could understand the report',
        ]
      ),
      cat(
        'rp-11',
        'Immediate actions',
        'On contact or compromise, did they act as briefed, keep control, and report the effect on the mission?',
        ['RECN-GRND-1004', 'RECN-GRND-2403', 'RECN-GRND-2413', 'RECN-GRND-2512', 'RECN-GRND-3002', 'RECN-GRND-4003', 'RECN-GRND-5002'],
        [
          'Critical event',
          'Roles: every member; team leader and assistant team leader direct',
          'Whether the threat or compromise was recognized and passed quickly',
          'Whether immediate actions kept accountability and control',
          'Whether they avoided action that was not required and that would wreck collection or survivability',
          'Whether movement, security, communications, and reporting changed after the event',
          'Whether the event and its effect on the mission were reported',
        ]
      ),
      cat(
        'rp-12',
        'Casualty and sustainment',
        'Did casualty care and sustainment keep security and the mission intact?',
        ['RECN-C2-2411', 'RECN-GRND-2006', 'RECN-LOG-3001', 'RECN-LOG-4001', 'RECN-LOG-4002', 'RECN-LOG-5001'],
        [
          'Critical event',
          'Roles: SARC when assigned, assistant team leader, team leader, and every member',
          'Whether injury, illness, heat or cold, dehydration, fatigue, or a sustainment problem was caught early',
          'Whether assessment, triage, treatment, and movement fit the scenario',
          'Whether security, accountability, communications, and the mission continued during the response',
          'Whether movement, timeline, extraction, resupply, and duties changed for the casualty',
          'Whether water, field sanitation, and team readiness were watched',
        ]
      ),
      cat(
        'rp-13',
        'Mission continuity',
        'After a change, did they keep collecting instead of drifting or stopping?',
        [],
        [
          'Roles: team leader, assistant team leader, radio operator, scouts, and SARC',
          'Whether weather, terrain, threat, equipment, communications, a casualty, or the timeline produced a real update',
          'Whether the update kept accountability, security, accurate reports, and control',
          'Whether collection stayed the focus after the disruption',
          'Whether they decided to continue, modify, stop, hand over, withdraw, or take a follow-on task',
          'Whether the change was clear to the patrol and to higher headquarters when it had to be',
        ]
      ),
      cat(
        'rp-14',
        'Withdrawal and reentry',
        'Did they leave when they should, keep what they collected, and account for everyone and everything on the way out?',
        [
          'RECN-GRND-5005',
          'RECN-GRND-1020',
          'RECN-GRND-1023',
          'RECN-GRND-2411',
          'RECN-GRND-2412',
          'RECN-GRND-4005',
          'RECN-GRND-4027',
          'RECN-GRND-5006',
          'RECN-GRND-5007',
          'RECN-C2-5001',
        ],
        [
          'Critical event',
          'Roles: every member; team leader and assistant team leader direct',
          'Whether observation stopped only for the mission, the time, the threat, or higher direction',
          'Whether withdrawal did not compromise people, equipment, or what was collected',
          'Whether security, navigation, communications, and accountability held during exfiltration',
          'Whether extraction, linkup, or reentry followed the order',
          'Whether people, weapons, radios, sensitive material, and collected information were accounted for',
        ]
      ),
      cat(
        'rp-15',
        'Debrief',
        'Did the debrief answer the intelligence requirements with what the patrol actually had?',
        ['RECN-C2-4002'],
        [
          'Roles: every member; team leader and assistant team leader lead',
          'Whether people, weapons, equipment, radios, imagery, logs, and products were accounted for',
          'Whether logs, notes, imagery, overlays, routes, and reports were pulled together',
          'Whether answers to the requirements stayed factual',
          'Whether maps, overlays, threat, routes, collection plans, and equipment status were updated',
          'Whether the after-action review named what to sustain, what failed, and what to retrain',
        ]
      ),
      cat(
        'rp-tl',
        'Team leader',
        'Did the team leader keep intent, control, and the collection requirement clear from start to debrief?',
        ['RECN-GRND-2402', 'RECN-C2-5002'],
        [
          'Whether intent, priorities, collection requirements, and decision points were communicated',
          'Whether control, accountability, tempo, and discipline held across every phase',
          'Whether orders and brief-backs were clear, and decisions came when conditions changed',
          'Whether recon and surveillance were directed without exposing the patrol without need',
          'Whether reports, communications, security, collection, casualties, withdrawal, and debrief stayed synchronized',
        ]
      ),
      cat(
        'rp-atl',
        'Assistant team leader',
        'Did the assistant team leader keep accountability and step in when the team leader could not?',
        [],
        [
          'Whether they assisted planning, preparation, equipment, rehearsals, and supervision',
          'Whether people, weapons, equipment, sustainment, and mission gear stayed accounted for',
          'Whether assigned tasks were supervised and understood',
          'Whether they took charge when directed or when the team leader was unavailable',
          'Whether reporting, discipline, and continuity held for the whole lane',
        ]
      ),
      cat(
        'rp-rto',
        'Radio operator',
        'Did the radio operator keep higher headquarters in contact and the log honest?',
        [],
        [
          'Whether assigned radios were prepared, tested, programmed, protected, and maintained',
          'Whether the team leader was advised on what the radios could and could not do',
          'Whether required communications were established and kept',
          'Whether reports and the communications log were accurate',
          'Whether failures and no-communications actions were handled',
        ]
      ),
      cat(
        'rp-arto',
        'Assistant radio operator',
        'Could the assistant radio operator take the net and still hold a security job?',
        [],
        [
          'Whether they helped prepare, inspect, program, power, and account for the radios',
          'Whether they understood the communications plan and the backup',
          'Whether they helped with checks, monitoring, the log, and reports',
          'Whether they took the radio operator’s duties when directed',
          'Whether security and patrol duties continued while they supported communications',
        ]
      ),
      cat(
        'rp-point',
        'Point',
        'Did the point keep the patrol on a route it could explain, and pass changes to the team leader in time?',
        [],
        [
          'Whether primary and alternate routes were understood',
          'Whether terrain, direction, distance, checkpoints, and danger areas stayed in view',
          'Whether movement used terrain and vegetation to limit detection',
          'Whether hazards, threat indicators, deviations, and obstacles were identified',
          'Whether route updates reached the team leader in time',
        ]
      ),
      cat(
        'rp-scout-1',
        'Scout 1',
        'Did this scout do the assigned job without breaking the patrol? Use N/A if this scout is not on the lane.',
        [],
        [
          'Record the specific duty under the unit SOP in the comment',
          'Whether the assigned security, observation, collection, navigation, or support task held patrol integrity',
          'Whether terrain, threat, target, and collection indicators were noticed',
          'Whether camouflage, noise, light, equipment, and movement discipline held',
          'Whether observations were reported accurately and promptly',
          'Whether assigned equipment stayed accounted for while helping the others',
        ]
      ),
      cat(
        'rp-scout-2',
        'Scout 2',
        'Did this scout do the assigned job without breaking the patrol? Use N/A if this scout is not on the lane.',
        [],
        [
          'Record the specific duty under the unit SOP in the comment',
          'Whether the assigned security, observation, collection, navigation, or support task held patrol integrity',
          'Whether terrain, threat, target, and collection indicators were noticed',
          'Whether camouflage, noise, light, equipment, and movement discipline held',
          'Whether observations were reported accurately and promptly',
          'Whether assigned equipment stayed accounted for while helping the others',
        ]
      ),
      cat(
        'rp-scout-3',
        'Scout 3',
        'Did this scout do the assigned job without breaking the patrol? Use N/A if this scout is not on the lane.',
        [],
        [
          'Record the specific duty under the unit SOP in the comment',
          'Whether the assigned security, observation, collection, navigation, or support task held patrol integrity',
          'Whether terrain, threat, target, and collection indicators were noticed',
          'Whether camouflage, noise, light, equipment, and movement discipline held',
          'Whether observations were reported accurately and promptly',
          'Whether assigned equipment stayed accounted for while helping the others',
        ]
      ),
      cat(
        'rp-sarc',
        'SARC',
        'Did the SARC keep the patrol healthy and the casualty plan usable? Use N/A if no SARC is assigned.',
        [],
        [
          'Whether the team leader was advised on health, casualty risk, readiness, and evacuation',
          'Whether assessment, triage, treatment, documentation, and movement fit the casualty',
          'Whether hydration, fatigue, sanitation, injury prevention, and exposure were watched',
          'Whether medical equipment stayed ready, supplied, and reachable on the move',
          'Whether medical action stayed tied to security, communications, accountability, and the mission',
        ]
      ),
    ],
  },
];

export function laneById(id: string): LaneDef | undefined {
  return LANES.find((l) => l.id === id);
}

export function laneTitle(id: string): string {
  return laneById(id)?.title || id;
}
